import { cn } from "@/lib/utils";
import * as React from "react";
import { ToggleTab, ToggleTabProps } from "./ToggleTab";

export type ToggleProps<T = any> = {
  className?: string;
  options?: {
    label: React.ReactNode;
    value: T;
    tooltip?: React.ReactNode;
  }[];
  testId?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  tabProps?: Omit<ToggleTabProps, "tab" | "isSelected" | "onSelect">;
  value?: T;
  onValueChange?: (value: T) => void;
  children?: React.ReactNode;
  errorMsg?: string;
};

export const Toggle = React.forwardRef<HTMLDivElement, ToggleProps>(
  (
    {
      className,
      options,
      testId = "toggle",
      fullWidth,
      disabled,
      readOnly,
      tabProps,
      value,
      onValueChange,
      children,
      errorMsg,
    },
    ref,
  ) => {
    const tabsContainerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      const tabElements = Array.from(tabsContainerRef.current?.children || []);

      tabElements.forEach((tabElement) => {
        delete (tabElement as HTMLButtonElement).dataset?.hideDivider;
      });
      tabElements.forEach((tabElement, i) => {
        if (
          (tabElement as HTMLButtonElement).dataset?.value === String(value)
        ) {
          const nextSibling = tabElements[i + 1] as HTMLButtonElement;

          if (nextSibling) {
            nextSibling.dataset.hideDivider = "true";
          }
        }
      });
    }, [value]);

    return (
      <div
        ref={ref}
        className={cn(
          "tw-min-w-[240px] disabled:tw-pointer-events-none",
          {
            "tw-w-full": fullWidth,
            "tw-pointer-events-none": disabled || readOnly,
          },
          className,
        )}
      >
        <div
          ref={tabsContainerRef}
          className={cn(
            "tw-flex tw-justify-between tw-rounded-6 tw-gap-4 tw-p-4 tw-bg-gray-200 tw-w-full tw-border tw-border-transparent tabs-container",
            {
              "tw-border-red-800": !!errorMsg,
            },
          )}
          data-testid={testId}
        >
          {children ||
            (options || []).map((tab) => (
              <ToggleTab
                key={tab.label?.toString()}
                tab={tab}
                disabled={disabled}
                isSelected={value === tab.value}
                onSelect={(selectedTab) => {
                  onValueChange?.(selectedTab);
                }}
                {...(tabProps || {})}
              />
            ))}
        </div>
      </div>
    );
  },
);
Toggle.displayName = "Toggle";
